import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useAppDispatch } from '../../../store/store';
import { Button, Grid, Box, CircularProgress, TextField, FormControl, InputLabel, Select, MenuItem, Typography, Stack, Tooltip } from '@mui/material';

import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import ArrowBackIosIcon from '@mui/icons-material/ArrowBackIos';
import { DesktopDatePicker } from '@mui/x-date-pickers/DesktopDatePicker';
import { isValid } from 'date-fns';
import { toDate, isDisplayableDate } from '../../../utils/dateHelpers';

import { getHgba1cAssessmentById, gotoNextMenu, gotoPreviousMenu, saveHgbA1CAssessment, setHgba1cByMemberBasicInfo } from '../../../store/assessment.slice';
import { RootState } from '../../../reducers';
import { TestingMachines, TestTimes } from '../../../utils/assessments';
import { copyObject, prepareTextFieldValue, preventTextInput } from '../../../utils/common';
import AlertDialog from '../../AlertDialog/AlertDialog';
import { StatusEnum } from '../../../Enum/StatusEnum';
import { AssessmentQuestions } from '../../../utils/assessmentQuestions';
import { isAIIcon } from '../Assessment';
import { getMemberBasicInfo } from '../../../store/memberDetail.slice';
import InfoIcon from '@mui/icons-material/Info';
import HelpPage from '../../../assets/documents/Assessment_HGBA1C_Lipids_Urinalysis_Diabete_Foot.pdf';

const classes = {
    formControl: {
        marginBottom: '25px',
        padding: '0!important',
    },
};

const HgbA1C: React.FC<{ memberId: string }> = ({ memberId }) => {
    const dispatch = useAppDispatch();
    const [showSaveAlert, setShowSaveAlert] = useState<boolean>(false);
    const { hgbA1CAssessment, isFetching } = useSelector((state: RootState) => state.assessment);
    const { engagementDraft } = useSelector((state: RootState) => state.documentEngagement);

    useEffect(() => {
        if (Boolean(engagementDraft.HgbA1CAssessmentId)) {
            dispatch(getHgba1cAssessmentById(engagementDraft.HgbA1CAssessmentId));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dispatch]);

    useEffect(() => {
        dispatch(getMemberBasicInfo(memberId, (e) => dispatch(setHgba1cByMemberBasicInfo(e))));
    }, [dispatch, memberId]);

    const handleOnChangeInput = (value: any, propsName: string, saveLocal = false) => {
        let data = copyObject(hgbA1CAssessment);
        data[propsName] = value;
        const aiFlagsByField: Record<string, string> = {
            TestDate: 'IsTestDateAi',
            TimeOfTest: 'IsTimeOfTestAi',
            LotNumTestCartridge: 'IsLotNumTestCartridgeAi',
            HgbA1CResult: 'IsHgbA1CResultAi',
            TestingMachine: 'IsTestingMachineAi',
        };
        const flagName = aiFlagsByField[propsName];
        if (flagName && Object.prototype.hasOwnProperty.call(data, flagName)) {
            data[flagName] = false;
        }

        dispatch(saveHgbA1CAssessment({ ...data, MemberId: memberId }, saveLocal));
    };

    const handleOnConfirmToNext = (isWithoutComplete = false) => {
        if (isWithoutComplete) {
            dispatch(saveHgbA1CAssessment({ ...hgbA1CAssessment, Status: StatusEnum.Completed }, false, true));
        } else {
            dispatch(gotoNextMenu());
        }
    };

    return (
        <>
            <Grid container direction="column" spacing={2} className="content-panel">
                <Stack direction={'row'} alignItems={'center'} spacing={1} sx={{ ml: 2 }}>
                    <Typography variant="h6">HgbA1C</Typography>
                    <Tooltip title="HgbA1C Help Document">
                        <InfoIcon color="primary" fontSize="small" cursor="pointer" onClick={() => window.open(`${HelpPage}`, '_blank')} />
                    </Tooltip>
                </Stack>
                <Grid className="content-form">
                    {isFetching ? (
                        <Box display="flex" justifyContent="center" className="loader-container">
                            <CircularProgress />
                        </Box>
                    ) : (
                        <Grid container spacing={4}>
                            <Grid size={{ xs: 12, md: 4 }}>
                                <Typography variant="h5" sx={{ mb: 4 }}>
                                    Member Information
                                </Typography>
                                <Box sx={classes.formControl}>
                                    <TextField label="Member Name" variant="outlined" size="small" fullWidth disabled value={hgbA1CAssessment.MemberName} />
                                </Box>
                                <Box sx={classes.formControl}>
                                    <DesktopDatePicker
                                        label="DOB"
                                        disabled
                                        value={Boolean(hgbA1CAssessment.DOB) ? new Date(hgbA1CAssessment.DOB) : null}
                                        format="MM/dd/yyyy"
                                        onChange={(date) => {}}
                                        slotProps={{ textField: { fullWidth: true, size: 'small' } }}
                                    />
                                </Box>
                                <Box sx={classes.formControl}>
                                    <TextField label="Age" variant="outlined" size="small" fullWidth disabled value={String(hgbA1CAssessment.Age)} />
                                </Box>
                            </Grid>
                            <Grid size={{ xs: 12, md: 8 }}>
                                <Typography variant="h5" sx={{ mb: 4 }}>
                                    Test Information
                                </Typography>

                                <Grid container spacing={2} rowGap={1.2}>
                                    <Grid size={{ xs: 12, md: 6 }}>
                                        <DesktopDatePicker
                                            label={
                                                <Typography component="span">
                                                    {AssessmentQuestions.HgbA1CAssessment_DateOfTest} {hgbA1CAssessment.IsTestDateAi && isAIIcon(hgbA1CAssessment.TestDate)}
                                                </Typography>
                                            }
                                            value={isDisplayableDate(hgbA1CAssessment.TestDate) ? toDate(hgbA1CAssessment.TestDate) : null}
                                            format="MM/dd/yyyy"
                                            minDate={new Date('2017-01-01')}
                                            onChange={(date) => {
                                                if (
                                                    (() => {
                                                        const d = toDate(date);
                                                        return d && isValid(d);
                                                    })() ||
                                                    !Boolean(date)
                                                ) {
                                                    handleOnChangeInput(date.toISOString(), 'TestDate');
                                                }
                                            }}
                                            slotProps={{ textField: { fullWidth: true, size: 'small' } }}
                                        />
                                    </Grid>
                                    <Grid size={{ xs: 12, md: 6 }}>
                                        <FormControl fullWidth size="small">
                                            <InputLabel id="testing-machine-select">
                                                <Typography component="span">
                                                    {AssessmentQuestions.HgbA1CAssessment_TestingMachine} {hgbA1CAssessment.IsTestingMachineAi && isAIIcon(hgbA1CAssessment.TestingMachine)}
                                                </Typography>
                                            </InputLabel>
                                            <Select
                                                labelId="testing-machine-select"
                                                id="testing-machine-select"
                                                disabled
                                                value={Boolean(hgbA1CAssessment.TestingMachine) ? String(hgbA1CAssessment.TestingMachine) : ''}
                                                label={AssessmentQuestions.HgbA1CAssessment_TestingMachine}
                                                name="TestingMachine"
                                                onChange={() => {}}
                                            >
                                                {TestingMachines.map((machine) => (
                                                    <MenuItem key={machine} value={machine}>
                                                        {machine}
                                                    </MenuItem>
                                                ))}
                                            </Select>
                                        </FormControl>
                                    </Grid>
                                    <Grid size={{ xs: 12, md: 6 }}>
                                        <FormControl fullWidth size="small">
                                            <InputLabel id="time-of-test-type-select">
                                                <Typography component="span">
                                                    {AssessmentQuestions.HgbA1CAssessment_TimeOfTest} {hgbA1CAssessment.IsTimeOfTestAi && isAIIcon(hgbA1CAssessment.TimeOfTest)}
                                                </Typography>
                                            </InputLabel>
                                            <Select
                                                labelId="time-of-test-type-select"
                                                id="time-of-test-type-select"
                                                value={Boolean(hgbA1CAssessment.TimeOfTest) ? String(hgbA1CAssessment.TimeOfTest) : ''}
                                                label={AssessmentQuestions.HgbA1CAssessment_TimeOfTest}
                                                name="TimeOfTest"
                                                onChange={(e) => handleOnChangeInput(e.target.value, e.target.name)}
                                            >
                                                <MenuItem value="">Select Test Time</MenuItem>
                                                {TestTimes.map((time) => (
                                                    <MenuItem key={time} value={time}>
                                                        {time}
                                                    </MenuItem>
                                                ))}
                                            </Select>
                                        </FormControl>
                                    </Grid>
                                    <Grid size={{ xs: 12, md: 6 }}>
                                        <TextField
                                            label={AssessmentQuestions.HgbA1CAssessment_LotNumTestCartridge}
                                            variant="outlined"
                                            size="small"
                                            fullWidth
                                            value={hgbA1CAssessment.LotNumTestCartridge}
                                            name="LotNumTestCartridge"
                                            onChange={(e) => handleOnChangeInput(e.target.value, e.target.name)}
                                            InputProps={{
                                                endAdornment: hgbA1CAssessment.IsLotNumTestCartridgeAi && isAIIcon(hgbA1CAssessment.LotNumTestCartridge),
                                            }}
                                        />
                                    </Grid>
                                    <Grid size={{ xs: 12, md: 6 }}>
                                        <TextField
                                            label={AssessmentQuestions.HgbA1CAssessment_HgbA1CResult}
                                            variant="outlined"
                                            size="small"
                                            fullWidth
                                            type="number"
                                            value={prepareTextFieldValue(hgbA1CAssessment.HgbA1CResult)}
                                            name="HgbA1CResult"
                                            onKeyPress={preventTextInput}
                                            inputProps={{ min: 0 }}
                                            onChange={(e) => handleOnChangeInput(e.target.value, e.target.name)}
                                            InputProps={{
                                                endAdornment: hgbA1CAssessment.IsHgbA1CResultAi && isAIIcon(hgbA1CAssessment.HgbA1CResult),
                                            }}
                                        />
                                    </Grid>
                                </Grid>
                            </Grid>
                        </Grid>
                    )}
                </Grid>
                {isFetching ? null : (
                    <Grid justifyContent="flex-end" className="content-footer">
                        <Button
                            type="submit"
                            variant="contained"
                            color="secondary"
                            sx={{ mr: 3 }}
                            onClick={() => dispatch(gotoPreviousMenu())}
                            startIcon={<ArrowBackIosIcon />}
                        >
                            Previous
                        </Button>
                        <Button type="submit" variant="contained" onClick={() => setShowSaveAlert(true)} endIcon={<ArrowForwardIosIcon />}>
                            Next
                        </Button>
                    </Grid>
                )}
            </Grid>
            {Boolean(engagementDraft.HgbA1CAssessmentId) ? (
                <AlertDialog
                    open={showSaveAlert}
                    title="HgbA1C Assessment"
                    cancelText="Go Back"
                    okText="Confirm"
                    onConfirm={() => handleOnConfirmToNext(true)}
                    onSkip={() => handleOnConfirmToNext()}
                    onClose={() => setShowSaveAlert(false)}
                    message="I confirm and acknowledged that HgbA1C assessment is completed."
                />
            ) : (
                <AlertDialog
                    open={showSaveAlert}
                    title="HgbA1C Assessment"
                    cancelText="No"
                    okText="Yes, Proceed"
                    onConfirm={() => handleOnConfirmToNext()}
                    onClose={() => setShowSaveAlert(false)}
                    message="Are you sure to proceed without completing HgbA1C assessment?"
                />
            )}
        </>
    );
};

export default HgbA1C;
